import type { MessageEvent, MessageEventRepository, ValidationResult } from './RepositoryInterfaces.js';

export class InMemoryMessageEventRepository implements MessageEventRepository {
  private events: Map<string, MessageEvent> = new Map();

  async findAll(): Promise<MessageEvent[]> {
    return Array.from(this.events.values()).sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }

  async findByProjectId(projectId: string): Promise<MessageEvent[]> {
    return (await this.findAll()).filter((e) => e.projectId === projectId);
  }

  async findByRunId(runId: string): Promise<MessageEvent[]> {
    return (await this.findAll()).filter((e) => e.runId === runId);
  }

  async findByDirection(direction: string): Promise<MessageEvent[]> {
    return (await this.findAll()).filter((e) => e.direction === direction);
  }

  async save(event: MessageEvent): Promise<MessageEvent> {
    this.events.set(event.id, event);
    return event;
  }

  async saveValidation(id: string, result: ValidationResult): Promise<void> {
    const event = this.events.get(id);
    if (event) {
      this.events.set(id, { ...event, validation: result.severity });
    }
  }

  seed(events: MessageEvent[]): void {
    events.forEach((e) => this.events.set(e.id, e));
  }

  subscribeToProject(projectId: string, callback: (events: MessageEvent[]) => void): () => void {
    const notify = () => {
      const events = Array.from(this.events.values())
        .filter((e) => e.projectId === projectId)
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 100);
      callback(events);
    };
    notify();
    const interval = setInterval(notify, 1000);
    return () => clearInterval(interval);
  }
}

export const createInMemoryMessageEventRepository = (): InMemoryMessageEventRepository =>
  new InMemoryMessageEventRepository();
